import type { ChildGuideItem } from '@tunein/web-api-client';
import { Accordion } from '@tunein/web-ui-components';
import classNames from 'clsx';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { sharingFromProfile } from 'src/common/actions/share';
import DisplayAd from 'src/common/components/ads/DisplayAd';
import FreeStarVideoAdContainer from 'src/common/components/ads/FreeStarVideoAdContainer';
import CollapsibleLabel from 'src/common/components/shared/CollapsibleLabel';
import {
  BOTTOM_BANNER_ENABLED,
  FREESTAR_ENABLED,
  FREESTAR_PRIMIS_VIDEO_ENABLED,
} from 'src/common/constants/experiments/ads';
import {
  LESS,
  LESS_INFORMATION,
  MORE,
  MORE_INFORMATION,
} from 'src/common/constants/localizations/profile';
import useActions from 'src/common/hooks/useActions';
import useExperiment from 'src/common/hooks/useExperiment';
import { usePalette } from 'src/common/hooks/usePalette';
import { useShowMore } from 'src/common/hooks/useShowMore';
import { useStores } from 'src/common/providers';
import {
  selectBreakpoint,
  selectDiscordState,
  selectIsDiscord,
  selectRouteProps,
} from 'src/common/selectors/app';
import cssVars from 'src/common/styles/variables';
import { getDisplayAdAttributes, gptSlotNames } from 'src/common/utils/ads/config';
import { useDetermineAdEligibility } from 'src/common/utils/ads/determineAdEligibility';
import determineSponsorshipEnabled from 'src/common/utils/ads/determineSponsorshipEnabled';
import { isMedium, isXLarge, isXXLarge } from 'src/common/utils/breakpoints';
import Banner from './Banner';
import MoreInformation from './MoreInformation';
import PageTitles from './PageTitles';
import PlayButton from './PlayButton';
import css from './ProfileHeader.module.scss';
import ProfileLogo from './ProfileLogo';
import { SocialControls } from './SocialControls';
import TeamLogos from './TeamLogos';

interface ProfileHeaderProps {
  guideItem: ChildGuideItem;
}

export function ProfileHeader({ guideItem }: ProfileHeaderProps) {
  const {
    bannerImage,
    heroHeader,
    image,
    title,
    subtitle,
    gameInfo,
    isPremium,
    genres,
    isAudiobook,
    isBoost,
    description,
    affiliate,
    actions: profileActions,
  } = guideItem;
  const { shouldShowMore, toggleShowMore, resetShowMore } = useShowMore();
  const breakpoint = useSelector(selectBreakpoint);
  const actions = useActions({ sharingFromProfile });
  const routeProps = useSelector(selectRouteProps);
  const { guideId } = routeProps?.guideContext || {};
  const canShowAds = useDetermineAdEligibility(guideId);
  const canExpand = guideItem?.actions?.expand?.canExpand;
  const shouldShowSocialControls = profileActions.follow && profileActions.share;
  const isDiscord = useSelector(selectIsDiscord);
  const discordState = useSelector(selectDiscordState);
  const isMediumBreakpoint = isMedium(breakpoint);
  const canFollow = profileActions?.follow?.canFollow;
  const hasPremiumBadge = isPremium && isAudiobook;
  const hasBoostBadge = isBoost;
  const { playerStore } = useStores();
  const tunedGuideId = playerStore.currentGuideItem()?.playableGuideId;
  const nowPlayingRejectReasonKey = playerStore.metadata()?.rejectReason;
  const isFreestarEnabled = useExperiment(FREESTAR_ENABLED);
  const isFreestarPrimisVideoEnabled = useExperiment(FREESTAR_PRIMIS_VIDEO_ENABLED);
  const isBottomBannerEnabled = useExperiment(BOTTOM_BANNER_ENABLED);
  const sponsorshipEnabled = useSelector(determineSponsorshipEnabled(guideId));
  const isDiscordWithBanner = isDiscord && !!bannerImage;
  const shouldShowInContentVideoAd =
    !isDiscordWithBanner &&
    canShowAds &&
    !isMedium(breakpoint) &&
    !sponsorshipEnabled &&
    isFreestarEnabled &&
    isFreestarPrimisVideoEnabled;
  const canShowDisplayAd =
    canShowAds &&
    ((isMediumBreakpoint && !isXLarge(breakpoint)) || isXXLarge(breakpoint)) &&
    !isBottomBannerEnabled;
  /* Big play button should be hidden for profiles that don't have a stream(canPlay=false)
  or no guideId for the tune call or podcast profile and experiment is off.
   */
  const hidePlayButton = !profileActions.play?.canPlay || !profileActions.play?.guideId;
  const palette = usePalette(guideId, image);
  const gradientBottom = isDiscord ? '--dark-grey' : '--content-area-background-color-hex';
  const canShowPaletteBackground = !!palette.vibrant && !gameInfo && !isDiscordWithBanner;
  const containerStyles = {
    ...(canShowPaletteBackground && {
      background: `linear-gradient(180deg, ${palette.vibrant}CC, ${cssVars[gradientBottom]})`,
    }),
  };

  useEffect(() => {
    if (guideId) {
      resetShowMore();
    }
  }, [guideId, resetShowMore]);

  const renderLogo = () => {
    if (gameInfo) {
      return <TeamLogos gameInfo={gameInfo} />;
    }

    if (!isDiscordWithBanner && (isMediumBreakpoint || !bannerImage)) {
      return (
        <ProfileLogo
          key={image}
          image={image}
          hasPremiumBadge={hasPremiumBadge}
          hasBoostBadge={hasBoostBadge}
          title={title}
          guideItem={guideItem}
        />
      );
    }

    return null;
  };

  const renderCollapsibleLabel = () => {
    const collapsibleLabelProps =
      !isDiscordWithBanner && isMediumBreakpoint
        ? {
            className: css.moreInfoButton,
            caretClassName: css.moreInfoCaret,
            moreText: MORE_INFORMATION,
            lessText: LESS_INFORMATION,
          }
        : {
            className: classNames(css.moreInfoButton, css.mobileMoreInfoButton),
            caretClassName: css.mobileMoreInfoCaret,
            moreText: MORE,
            lessText: LESS,
          };

    return (
      <div
        className={classNames(css.collapsibleLabelContainer, {
          [css.expandedInfo]: shouldShowMore,
          [css.isDiscord]: isDiscord,
          [css.withoutPlayButton]: hidePlayButton,
          [css.isDiscordWithBanner]: isDiscordWithBanner,
        })}
      >
        <CollapsibleLabel
          shouldShowMore={shouldShowMore}
          onMoreInfoClick={toggleShowMore}
          {...collapsibleLabelProps}
        />
      </div>
    );
  };

  const accordionSection = (
    <div className={classNames('row', css.collapsibleLabelSection)}>
      <div className="col-sm-12">
        {canExpand && (
          <>
            {!isDiscordWithBanner && isMediumBreakpoint && renderCollapsibleLabel()}
            {/* @ts-ignore - className is a required prop, but we'll replace this later with our new UI library */}
            <Accordion data-testid="accordionComponent" isExpanded={shouldShowMore}>
              <div
                data-testid="moreInfoContent"
                className={classNames(css.moreInfoContent, {
                  [css.isDiscord]: isDiscord,
                })}
              >
                <MoreInformation guideItem={guideItem} />
              </div>
            </Accordion>
          </>
        )}
        {shouldShowInContentVideoAd && <FreeStarVideoAdContainer />}
        {!isDiscordWithBanner && isMediumBreakpoint && (
          <div
            className={classNames(css.dividerContainer, {
              [css.noCollapsibleLabelContainer]: !canExpand,
              [css.isDiscord]: isDiscord,
            })}
          >
            <hr />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        style={containerStyles}
        className={classNames(css.container, {
          [css.headerContainerDefault]: !isDiscordWithBanner && (gameInfo || palette.error),
          [css.paddingTop]: !bannerImage && !canShowDisplayAd && !isDiscord,
        })}
      >
        {bannerImage ? (
          <Banner
            src={bannerImage}
            hasPremiumBadge={hasPremiumBadge}
            hasBoostBadge={hasBoostBadge}
            logoImage={image}
            showLogoImage={!!bannerImage && (isDiscord || (!gameInfo && !isMediumBreakpoint))}
            heroHeader={heroHeader}
            title={title}
            isDiscord={isDiscord}
            className={classNames(css.banner, {
              [css.hasHeroHeader]: !!heroHeader || isDiscordWithBanner,
              [css.bannerMaxWidth]: isDiscordWithBanner,
            })}
            guideItem={guideItem}
          />
        ) : (
          canShowDisplayAd && (
            <DisplayAd
              style={{ paddingBottom: '45px' }}
              guideId={routeProps?.guideContext?.guideId}
              matchUrl={routeProps.matchUrl}
              isProfile
              {...getDisplayAdAttributes(gptSlotNames.profile_top, isFreestarEnabled)}
            />
          )
        )}

        <div
          className={classNames(css.contentContainer, {
            [css.hasBannerImage]: !!bannerImage,
            [css.discord]: isDiscord,
          })}
        >
          {renderLogo()}
          <div
            className={classNames(css.titleActionButtonsContainer, {
              [css.isDiscord]: isDiscord,
              [css.isDiscordWithBanner]: isDiscordWithBanner,
            })}
          >
            <PageTitles
              className={classNames(css.titleContainer, {
                [css.isTeamTitle]: gameInfo,
                [css.hasDescription]: !!description,
                [css.isDiscordWithBanner]: isDiscordWithBanner,
              })}
              title={title}
              subtitle={subtitle}
              description={description}
              affiliate={affiliate}
              genres={genres}
              isGame={!!gameInfo}
              isDiscord={isDiscord}
            />
            <div
              className={classNames(css.actionButtonsContainer, {
                [css.isTeamTitle]: gameInfo,
                [css.isDiscordWithBanner]: isDiscordWithBanner,
              })}
            >
              {canExpand &&
                (isDiscordWithBanner || !isMediumBreakpoint) &&
                renderCollapsibleLabel()}
              {!hidePlayButton && (
                <div
                  className={classNames(css.profilePlayButton, {
                    [css.withoutFavorites]: !canFollow,
                    [css.withoutMoreInfo]: !canExpand,
                    [css.isDiscordWithBanner]: isDiscordWithBanner,
                    [css.isDisabled]:
                      isDiscord &&
                      (!discordState?.canControlPlayback ||
                        (nowPlayingRejectReasonKey && guideItem.guideId === tunedGuideId)),
                  })}
                >
                  <PlayButton
                    guideItem={guideItem}
                    breakpoint={breakpoint}
                    bannerImage={bannerImage}
                  />
                </div>
              )}
              {shouldShowSocialControls && (
                <SocialControls
                  canFollow={canFollow}
                  hidePlayButton={hidePlayButton}
                  breakpoint={breakpoint}
                  guideItem={guideItem}
                  handleSharing={() => {
                    actions.sharingFromProfile(guideItem);
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {isDiscord && accordionSection}
      </div>
      {!isDiscord && accordionSection}
    </>
  );
}
